import { useEffect, useState } from 'react';
import type React from 'react';

import {
  useGetDepartment,
  usePatchDepartment,
} from '@vlabs/api/artemy/client';
import type { PatchDepartmentRequest } from '@vlabs/api/artemy/model';
import { Switch } from '@vlabs/uikit/components/controls/switch/Switch';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useAssignListsToAllInDepartmentTranslation } from './i18n';
import type { AssignListsToAllInDepartmentSwitchProps } from './types';

const displayName = 'AssignListsToAllInDepartmentSwitch';
const testid = buildTestid(displayName);

function AssignListsToAllInDepartmentSwitch({
  departmentId,
  listIds,
  'data-testid': dataTestId = undefined,
}: AssignListsToAllInDepartmentSwitchProps): React.JSX.Element {
  const { t } = useAssignListsToAllInDepartmentTranslation();
  const [checked, setChecked] = useState(false);
  const [listId] = listIds;

  const getDepartment = useGetDepartment(departmentId);
  const patchDepartment = usePatchDepartment();

  const departmentLists = getDepartment.data?.lists ?? [];
  const currentList = departmentLists.find(({ list_id }) => list_id === listId);
  const isDisabled = getDepartment.isLoading
    || patchDepartment.isPending
    || currentList === undefined;

  useEffect(() => {
    if (getDepartment.isSuccess) {
      setChecked(!!currentList?.open);
    }
  }, [currentList?.open, getDepartment.isSuccess]);

  const handleChange = (newChecked: boolean): void => {
    const previousChecked = checked;

    const data: PatchDepartmentRequest = {
      lists: departmentLists.map((list) => {
        if (list.list_id !== listId) {
          return list;
        }

        return { ...list, open: newChecked };
      }),
    };

    setChecked(newChecked);

    patchDepartment.mutate(
      { departmentId, data },
      {
        onSuccess: () => {
          toast.success(t('toast_success'));
        },
        onError: () => {
          setChecked(previousChecked);
          toast.error(t('toast_error'));
        },
      },
    );
  };

  return (
    <Switch
      disabled={isDisabled}
      id="assignListsToAllInDepartment"
      label={t('label') as unknown as string}
      name="assignListsToAllInDepartment"
      onChange={handleChange}
      value={checked}
      {...testid(dataTestId)}
    />
  );
}

export { AssignListsToAllInDepartmentSwitch };
